import { useEffect, useRef, useState } from 'react';
import { useToastInline } from '../components/ToastNotifications';
import { FILE_TYPE_BRAND_LOGO, FILE_TYPE_PRODUCT_IMAGE } from '../constants';
import { getErrorMessage } from '../utils/requestErrors';
import { uploadFileId } from './useFiles';

type Image = { id: string | number | null; name: string; url: string };

const emptyImage: Image = { id: null, name: '', url: '' };

type ImageUploadOptions = {
  organizationId?: string | number;
  kind: 'product' | 'brand';
};

export function useImageUpload({ organizationId, kind }: ImageUploadOptions) {
  const [committed, setCommitted] = useState<Image>(emptyImage);
  const [pending, setPending] = useState<{ name: string; url: string } | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [isPending, setIsPending] = useState(false);
  const { message: error, showToastInline, hideToastInline } = useToastInline();
  const committedRef = useRef<Image>(emptyImage);
  const baselineRef = useRef<Image>(emptyImage);
  const pendingRef = useRef<{ name: string; url: string } | null>(null);
  const ownedUrls = useRef(new Set<string>());
  const requestId = useRef(0);
  const contextId = useRef(0);
  const modified = useRef(false);
  const mounted = useRef(true);
  const pendingRefState = useRef(false);

  const release = (url: string) => {
    if (ownedUrls.current.delete(url)) URL.revokeObjectURL(url);
  };

  const commit = (image: Image) => {
    release(committedRef.current.url);
    committedRef.current = image;
    setCommitted(image);
  };

  const setPreview = (preview: { name: string; url: string } | null) => {
    if (pendingRef.current) release(pendingRef.current.url);
    pendingRef.current = preview;
    setPending(preview);
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestId.current += 1;
      ownedUrls.current.forEach((url) => URL.revokeObjectURL(url));
      ownedUrls.current.clear();
    };
  }, []);

  const reset = () => {
    requestId.current += 1;
    contextId.current += 1;
    modified.current = false;
    baselineRef.current = emptyImage;
    pendingRefState.current = false;
    setIsPending(false);
    setPreview(null);
    commit(emptyImage);
    hideToastInline();
    setFileInputKey((key) => key + 1);
    return contextId.current;
  };

  const hydrate = (context: number, image: Partial<Image>) => {
    if (context !== contextId.current || !mounted.current) return;
    const next: Image = { id: image.id ?? null, name: image.name || '', url: image.url || '' };
    baselineRef.current = next;
    if (!modified.current && !pendingRefState.current) commit(next);
  };

  const remove = () => {
    requestId.current += 1;
    modified.current = true;
    pendingRefState.current = false;
    setIsPending(false);
    setPreview(null);
    commit(emptyImage);
    hideToastInline();
    setFileInputKey((key) => key + 1);
  };

  const select = async (file?: File) => {
    if (!file) return;
    const currentRequest = ++requestId.current;
    const currentContext = contextId.current;
    hideToastInline();
    if (!organizationId) return;
    const url = URL.createObjectURL(file);
    ownedUrls.current.add(url);
    setPreview({ name: file.name, url });
    pendingRefState.current = true;
    setIsPending(true);
    try {
      const id = await uploadFileId({
        organizationId,
        fileType: kind === 'product' ? FILE_TYPE_PRODUCT_IMAGE : FILE_TYPE_BRAND_LOGO,
        mimeType: file.type,
        file,
      });
      if (!mounted.current || currentRequest !== requestId.current || currentContext !== contextId.current) return;
      modified.current = true;
      pendingRefState.current = false;
      setIsPending(false);
      pendingRef.current = null;
      setPending(null);
      commit({ id, name: file.name, url });
    } catch (uploadError) {
      if (!mounted.current || currentRequest !== requestId.current || currentContext !== contextId.current) return;
      console.error(uploadError);
      pendingRefState.current = false;
      setIsPending(false);
      setPreview(null);
      if (!modified.current) commit(baselineRef.current);
      setFileInputKey((key) => key + 1);
      if (kind === 'product') {
        const match = /too large.*?Only images ([\d.]+)\s*MB/i.exec(getErrorMessage(uploadError, ''));
        showToastInline(match
          ? `This image is too large to upload. Only images ${match[1]} MB or less can be uploaded.`
          : 'This image could not be uploaded. Please try again.');
      } else {
        showToastInline('This logo could not be uploaded. Please try again.');
      }
    }
  };

  return {
    id: committed.id,
    name: pending?.name ?? committed.name,
    url: pending?.url ?? committed.url,
    isPending,
    error,
    dismissError: hideToastInline,
    fileInputKey,
    reset,
    hydrate,
    select,
    remove,
  };
}
